import React, { useState, useEffect, useCallback } from 'react'
import { policiesAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import {
  FileText,
  Upload,
  Eye,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  ScrollText,
  ChevronDown,
} from 'lucide-react'

const POLICY_TYPES = [
  { value: 'PRIVACY_POLICY', label: 'Política de Privacidade', icon: Shield },
  { value: 'CONDITIONS_POLICY', label: 'Termos e Condições', icon: ScrollText },
]

const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.txt'

const formatDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const PolicyTypeBadge = ({ type }) => {
  const config = POLICY_TYPES.find((t) => t.value === type) || {}
  const Icon = config.icon || FileText
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
      <Icon className="h-3 w-3" />
      {config.label || type}
    </span>
  )
}

const UploadModal = ({ onUpload, onClose }) => {
  const [file, setFile] = useState(null)
  const [policyType, setPolicyType] = useState('PRIVACY_POLICY')
  const [title, setTitle] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    setFile(selected)
    setError(null)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const dropped = e.dataTransfer.files?.[0]
    if (!dropped) return
    setFile(dropped)
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!file) return setError('Selecione um arquivo')

    setUploading(true)
    setError(null)
    try {
      await onUpload(file, policyType, title)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Falha no envio')
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-admin-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-admin-900">Enviar documento de política</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-admin-400 hover:text-admin-600"
              aria-label="Fechar"
              tabIndex={0}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label htmlFor="policy-type" className="block text-sm font-medium text-admin-700">
                Tipo de política
              </label>
              <div className="relative mt-1">
                <select
                  id="policy-type"
                  value={policyType}
                  onChange={(e) => setPolicyType(e.target.value)}
                  className="input appearance-none pr-10"
                >
                  {POLICY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-400" />
              </div>
            </div>

            <div>
              <label htmlFor="policy-title" className="block text-sm font-medium text-admin-700">
                Título <span className="text-admin-400">(opcional)</span>
              </label>
              <input
                id="policy-title"
                type="text"
                className="input mt-1"
                placeholder="ex.: Política de Privacidade março 2026"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">Documento</label>
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-admin-300 px-6 py-8 text-center transition-colors hover:border-primary-400 hover:bg-primary-50/30"
              >
                <Upload className="mx-auto h-10 w-10 text-admin-300" />
                <p className="mt-3 text-sm text-admin-600">
                  {file ? (
                    <span className="font-medium text-primary-600">{file.name}</span>
                  ) : (
                    <>
                      Arraste e solte ou{' '}
                      <label
                        htmlFor="policy-file"
                        className="cursor-pointer font-medium text-primary-600 hover:text-primary-700"
                        tabIndex={0}
                        role="button"
                        aria-label="Procurar arquivos"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') document.getElementById('policy-file')?.click()
                        }}
                      >
                        procure no computador
                      </label>
                    </>
                  )}
                </p>
                <p className="mt-1 text-xs text-admin-400">PDF, DOC, DOCX ou TXT</p>
                <input
                  id="policy-file"
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>
          </form>

          <div className="flex items-center justify-end gap-3 border-t border-admin-200 px-6 py-4">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || !file}
              className="btn btn-primary flex items-center gap-2"
            >
              {uploading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? 'Enviando...' : 'Enviar e criar versão'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const ContentPreviewModal = ({ policy, onClose }) => {
  const [fullPolicy, setFullPolicy] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await policiesAPI.getOne(policy.id)
        setFullPolicy(res.data?.data || res.data)
      } catch {
        setFullPolicy({ ...policy, content: 'Falha ao carregar o conteúdo.' })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [policy.id])

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="relative w-full max-w-3xl rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-admin-200 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-admin-900">
                {policy.title || 'Documento de política'}
              </h2>
              <div className="mt-1 flex items-center gap-2">
                <PolicyTypeBadge type={policy.policy_type} />
                <span className="text-xs text-admin-500">Versão {policy.version}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-admin-400 hover:text-admin-600"
              aria-label="Fechar pré-visualização"
              tabIndex={0}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
              </div>
            ) : (
              <pre className="whitespace-pre-wrap break-words text-sm text-admin-700 font-sans leading-relaxed">
                {fullPolicy?.content}
              </pre>
            )}
          </div>

          <div className="flex items-center justify-end border-t border-admin-200 px-6 py-4">
            <button onClick={onClose} className="btn btn-secondary">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const PoliciesPage = () => {
  const { isOwner } = useAuth()
  const [policies, setPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [previewPolicy, setPreviewPolicy] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [filterType, setFilterType] = useState('')

  const loadPolicies = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {}
      if (filterType) params.type = filterType
      const res = await policiesAPI.getAll(params)
      setPolicies(res.data?.data || [])
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao carregar as políticas')
    } finally {
      setLoading(false)
    }
  }, [filterType])

  useEffect(() => {
    loadPolicies()
  }, [loadPolicies])

  useEffect(() => {
    if (!successMsg) return
    const timer = setTimeout(() => setSuccessMsg(null), 4000)
    return () => clearTimeout(timer)
  }, [successMsg])

  const handleUpload = async (file, policyType, title) => {
    await policiesAPI.upload(file, policyType, title)
    setShowUpload(false)
    setSuccessMsg('Política enviada com sucesso')
    loadPolicies()
  }

  const handleActivate = async (policy) => {
    try {
      await policiesAPI.activate(policy.id)
      setSuccessMsg(`A versão ${policy.version} agora está ativa`)
      loadPolicies()
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao ativar a política')
    }
  }

  const handleDelete = async (policy) => {
    try {
      await policiesAPI.remove(policy.id)
      setDeleteConfirm(null)
      setSuccessMsg(`Versão ${policy.version} excluída`)
      loadPolicies()
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao excluir a política')
    }
  }

  const groupedPolicies = POLICY_TYPES.map((type) => ({
    ...type,
    items: policies.filter((p) => p.policy_type === type.value),
  }))

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="text-admin-500">Carregando políticas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-admin-900 flex items-center gap-2">
            <FileText className="h-7 w-7 text-primary-600" />
            Configuração de privacidade e políticas
          </h1>
          <p className="mt-1 text-sm text-admin-500">
            Envie e gerencie os documentos de política de privacidade e de termos e condições. Cada envio cria uma nova versão.
          </p>
        </div>
        {isOwner && (
        <button
          onClick={() => setShowUpload(true)}
          className="btn btn-primary flex items-center gap-2"
          aria-label="Enviar nova política"
          tabIndex={0}
        >
          <Upload className="h-4 w-4" />
          Enviar documento
        </button>
        )}
      </div>

      {/* Success */}
      {successMsg && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-sm text-green-700">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          {successMsg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
            aria-label="Dispensar erro"
            tabIndex={0}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-admin-600">Filtro:</span>
        <button
          onClick={() => setFilterType('')}
          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
            filterType === ''
              ? 'bg-primary-100 text-primary-700'
              : 'bg-admin-100 text-admin-600 hover:bg-admin-200'
          }`}
          aria-label="Mostrar todas as políticas"
          tabIndex={0}
        >
          Todas
        </button>
        {POLICY_TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => setFilterType(t.value)}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              filterType === t.value
                ? 'bg-primary-100 text-primary-700'
                : 'bg-admin-100 text-admin-600 hover:bg-admin-200'
            }`}
            aria-label={`Filtrar por ${t.label}`}
            tabIndex={0}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Policy groups */}
      {(filterType ? groupedPolicies.filter((g) => g.value === filterType) : groupedPolicies).map(
        (group) => (
          <div key={group.value} className="space-y-3">
            <div className="flex items-center gap-2">
              <group.icon className="h-5 w-5 text-admin-500" />
              <h2 className="text-lg font-semibold text-admin-800">{group.label}</h2>
              <span className="rounded-full bg-admin-100 px-2 py-0.5 text-xs font-medium text-admin-600">
                {group.items.length} {group.items.length !== 1 ? 'versões' : 'versão'}
              </span>
            </div>

            {group.items.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-admin-200 p-8 text-center">
                <FileText className="mx-auto h-10 w-10 text-admin-300" />
                <p className="mt-3 text-sm text-admin-500">
                  Nenhum documento de {group.label} enviado ainda.
                </p>
                {isOwner && (
                <button
                  onClick={() => setShowUpload(true)}
                  className="btn btn-primary mt-3 text-sm"
                  aria-label={`Enviar ${group.label}`}
                  tabIndex={0}
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5 inline" />
                  Enviar
                </button>
                )}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-admin-200 bg-white">
                <table className="min-w-full divide-y divide-admin-200">
                  <thead className="bg-admin-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-500">
                        Versão
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-500">
                        Título
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-500">
                        Arquivo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-500">
                        Criado em
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-admin-500">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-admin-100">
                    {group.items.map((policy) => (
                      <tr key={policy.id} className="hover:bg-admin-50/50 transition-colors">
                        <td className="px-4 py-3 text-sm font-mono font-medium text-admin-900">
                          v{policy.version}
                        </td>
                        <td className="px-4 py-3 text-sm text-admin-700">
                          {policy.title || <span className="text-admin-400 italic">Sem título</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-admin-500 truncate max-w-[200px]">
                          {policy.original_filename || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {policy.is_active ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
                              <CheckCircle className="h-3 w-3" />
                              Ativa
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-admin-100 px-2.5 py-0.5 text-xs font-medium text-admin-500">
                              <Clock className="h-3 w-3" />
                              Inativa
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-admin-500">
                          {formatDate(policy.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setPreviewPolicy(policy)}
                              className="rounded-md p-1.5 text-admin-400 hover:bg-admin-100 hover:text-admin-600"
                              aria-label={`Pré-visualizar versão ${policy.version}`}
                              tabIndex={0}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {isOwner && !policy.is_active && (
                              <button
                                onClick={() => handleActivate(policy)}
                                className="rounded-md p-1.5 text-admin-400 hover:bg-green-50 hover:text-green-600"
                                aria-label={`Ativar versão ${policy.version}`}
                                tabIndex={0}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                            {isOwner && (
                            <button
                              onClick={() => setDeleteConfirm(policy)}
                              className="rounded-md p-1.5 text-admin-400 hover:bg-red-50 hover:text-red-500"
                              aria-label={`Excluir versão ${policy.version}`}
                              tabIndex={0}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      )}

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal onUpload={handleUpload} onClose={() => setShowUpload(false)} />
      )}

      {/* Preview Modal */}
      {previewPolicy && (
        <ContentPreviewModal
          policy={previewPolicy}
          onClose={() => setPreviewPolicy(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setDeleteConfirm(null)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-admin-900">Excluir versão da política</h3>
            <p className="mt-2 text-sm text-admin-600">
              Tem certeza de que deseja excluir permanentemente a{' '}
              <strong>
                versão {deleteConfirm.version}
                {deleteConfirm.title ? ` — ${deleteConfirm.title}` : ''}
              </strong>
              ? Esta ação não pode ser desfeita.
            </p>
            {deleteConfirm.is_active && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                Esta é a versão ativa no momento. Ao excluí-la, não haverá nenhuma política ativa deste tipo.
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn btn-secondary">
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
